/**
 * The AI model catalogue, as this console sees it.
 *
 * Three surfaces map onto three authorities, and they are not interchangeable:
 *
 *   /ai/models/mine*      — what I may spend, and my own key if BYOK is on
 *   /ai/models/tenant*    — what my account publishes to its own people
 *   /ai/models/platform*  — what staff publish, and who may spend it
 *
 * When the server has not armed ``AI_PLATFORM_ENABLED``, catalogue routes stay
 * unmounted (404). Soft≠Live Page152 exposes always-on GET /ai/models/status so
 * CMC never confuses Soft "no models" with an unarmed deployment. Catalogue
 * 404 is still shaped as armed:false for residual callers.
 */
import { api, path, useApi, useAction } from "./api.js";

/* The contract's path_template collapses every ``{…}`` to ``{param}``, so
   callers pass values positionally. Named params would look right and fail. */
const p = (id, ...pathParams) => path(id, pathParams.length ? { pathParams } : {});

const MINE            = () => p("get__ai_models_mine");
const EFFECTIVE       = () => p("get__ai_models_mine_effective");
const PREFERENCE      = () => p("put__ai_models_mine_preference");
const MY_KEY          = () => p("get__ai_models_mine_key");
const PUT_MY_KEY      = () => p("put__ai_models_mine_key");
const DEL_MY_KEY      = () => p("delete__ai_models_mine_key");
const TENANT          = () => p("get__ai_models_tenant");
const PUT_TENANT      = () => p("put__ai_models_tenant");
const DEL_TENANT      = (id) => p("delete__ai_models_tenant_p", id);
const TENANT_BYOK     = () => p("get__ai_models_byok-policy_tenant");
const PUT_TENANT_BYOK = () => p("put__ai_models_byok-policy_tenant");
const PLATFORM        = () => p("get__ai_models_platform");
const PUT_PLATFORM    = () => p("put__ai_models_platform");
const DEL_PLATFORM    = (id) => p("delete__ai_models_platform_p", id);
const PLATFORM_BYOK   = () => p("get__ai_models_byok-policy_platform");
const PUT_PLATFORM_BYOK = () => p("put__ai_models_byok-policy_platform");
const GRANTS_T        = (id) => p("get__ai_models_tenant_p_grants", id);
const POST_GRANT_T    = (id) => p("post__ai_models_tenant_p_grants", id);
const DEL_GRANT_T     = (id, g) => p("delete__ai_models_tenant_p_grants_p", id, g);
const GRANTS_P        = (id) => p("get__ai_models_platform_p_grants", id);
const POST_GRANT_P    = (id) => p("post__ai_models_platform_p_grants", id);
const DEL_GRANT_P     = (id, g) => p("delete__ai_models_platform_p_grants_p", id, g);
const PROBE_TENANT    = (id) => {
  try {
    return p("post__ai_models_tenant_p_probe", id);
  } catch {
    return `/ai/models/tenant/${encodeURIComponent(id)}/probe`;
  }
};

/** ``useApi`` stores errors as ``"${status} ${detail}"`` strings. */
function shapeError(err) {
  const msg = String((err && err.message) || err || "");
  const status = Number((msg.match(/^(\d{3})\b/) || [])[1]) || 0;
  if (status === 404) {
    return { armed: false, message: "The model catalogue is not armed on this deployment." };
  }
  if (status === 403) {
    return { armed: true, forbidden: true, message: msg.replace(/^\d{3}\s*/, "") || "You are not allowed to do that." };
  }
  if (status === 503) {
    return { armed: true, unavailable: true, message: msg.replace(/^\d{3}\s*/, "") || "The catalogue is temporarily unreachable." };
  }
  return { armed: true, message: msg || "The catalogue did not answer." };
}

/**
 * One catalogue list. ``null`` which means "do not ask" — used when a screen
 * that only an admin can open is being rendered by someone who is not one.
 */
export function useCatalogue(which) {
  // Always call the hook — a null path is "do not ask", not "skip the hook".
  const url = which === "platform" ? PLATFORM()
            : which === "tenant"   ? TENANT()
            : which === "mine"     ? MINE()
            : which === "my-key"   ? MY_KEY()
            : null;
  const r = useApi(url);
  if (!which) return { models: [], loading: false, error: "", armed: true, reload: r.reload };
  if (r.error) {
    const shaped = shapeError(r.error);
    return {
      models: [],
      loading: r.loading,
      error: shaped.message,
      armed: shaped.armed !== false,
      forbidden: !!shaped.forbidden,
      reload: r.reload,
    };
  }
  return {
    models: (r.data && r.data.models) || [],
    loading: r.loading,
    error: "",
    armed: true,
    reload: r.reload,
  };
}

export function useEffective(purpose) {
  /* Soft≠Live Page152: purpose === false skips the Live bus (status posture gate). */
  if (purpose === false || purpose === null) {
    const r = useApi(null);
    return { effective: null, loading: false, error: "", armed: false, reload: r.reload };
  }
  const q = purpose ? `?purpose=${encodeURIComponent(purpose)}` : "";
  const r = useApi(EFFECTIVE() + q);
  if (r.error) {
    const shaped = shapeError(r.error);
    return { effective: null, loading: r.loading, error: shaped.message, armed: shaped.armed !== false, reload: r.reload };
  }
  return {
    effective: (r.data && r.data.effective) || null,
    loading: r.loading,
    error: "",
    armed: true,
    reload: r.reload,
  };
}

export function useByok(scope) {
  // Always call the hook; a null scope is "do not ask".
  const url = scope === "platform" ? PLATFORM_BYOK()
            : scope === "tenant"   ? TENANT_BYOK()
            : null;
  const r = useApi(url);
  if (!scope) return { policy: null, loading: false, error: "", armed: true, reload: r.reload };
  if (r.error) {
    const shaped = shapeError(r.error);
    return { policy: null, loading: r.loading, error: shaped.message, armed: shaped.armed !== false, reload: r.reload };
  }
  return { policy: r.data || null, loading: r.loading, error: "", armed: true, reload: r.reload };
}

export function useModelWrites(which) {
  const action = useAction();
  return {
    busy: action.busy,
    error: action.error,
    envelope: action.envelope,
    clear: action.clear,
    async save(body) {
      const url = which === "platform" ? PUT_PLATFORM()
                : which === "tenant"   ? PUT_TENANT()
                : PUT_MY_KEY();
      return action.run(url, { method: "PUT", body });
    },
    async remove(modelId) {
      if (which === "platform") return action.run(DEL_PLATFORM(modelId), { method: "DELETE" });
      if (which === "tenant")   return action.run(DEL_TENANT(modelId), { method: "DELETE" });
      return action.run(DEL_MY_KEY() + `?model_id=${encodeURIComponent(modelId)}`, { method: "DELETE" });
    },
    async setPreference(purpose, modelId) {
      return action.run(PREFERENCE(), {
        method: "PUT",
        body: { purpose: purpose || "", model_id: modelId || "" },
      });
    },
    async setByok(allow) {
      const url = which === "platform" ? PUT_PLATFORM_BYOK() : PUT_TENANT_BYOK();
      return action.run(url, { method: "PUT", body: { allow_byok: !!allow } });
    },
    async grant(modelId, granteeKind, granteeId) {
      const url = which === "platform" ? POST_GRANT_P(modelId) : POST_GRANT_T(modelId);
      return action.run(url, {
        method: "POST",
        body: { grantee_kind: granteeKind, grantee_id: granteeId || "" },
      });
    },
    async revoke(modelId, grantId) {
      const url = which === "platform" ? DEL_GRANT_P(modelId, grantId) : DEL_GRANT_T(modelId, grantId);
      return action.run(url, { method: "DELETE" });
    },
    async probe(modelId) {
      if (which !== "tenant") return null;
      return action.run(PROBE_TENANT(modelId), { method: "POST", body: {} });
    },
  };
}

export function useGrants(which, modelId) {
  const url = !modelId ? null
            : which === "platform" ? GRANTS_P(modelId)
            : GRANTS_T(modelId);
  const r = useApi(url);
  return {
    grants: (r.data && r.data.grants) || [],
    loading: r.loading,
    error: r.error ? shapeError(r.error).message : "",
    reload: r.reload,
  };
}

/** Where the weights sit — derived from the provider, never stored. */
export function kindOf(model) {
  if (!model) return "hosted";
  if (model.provider === "ollama" || model.cost_class === "local") return "local";
  return "hosted";
}

/**
 * Soft Forge / board-ask jobs → live preference purposes (product call A).
 * Global by purpose; Settings → Model access is the single write surface.
 */
export const JOB_PURPOSE = Object.freeze({
  design: "forge.design",
  clarify: "forge.clarify",
  review: "forge.review",
  summarise: "incident.summarise",
  onboard: "edge.onboard",
});

/** Operator-facing job titles (not a second catalogue). */
export const JOB_LABEL = Object.freeze({
  design: "Designing a capability",
  clarify: "Asking what you meant",
  review: "Reviewing generated code",
  summarise: "Summarising an incident",
  onboard: "Classifying on the device",
});

/** Project an effective row into the small shape Forge / node3d already paint. */
export function viewOfEffective(effective, jobId) {
  const purpose = JOB_PURPOSE[jobId] || "";
  const jobLabel = JOB_LABEL[jobId] || jobId || "model";
  if (!effective) {
    return {
      label: "no model for this purpose",
      model_id: "",
      kind: "none",
      state: "key_missing",
      model: "",
      purpose,
      jobLabel,
    };
  }
  const ready = !!(effective.has_secret || effective.provider === "ollama");
  return {
    label: effective.label || effective.text_model || effective.model_id || "model",
    model_id: effective.model_id || "",
    kind: kindOf(effective),
    state: ready ? "ready" : "key_missing",
    model: effective.text_model || effective.vision_model || "",
    purpose,
    jobLabel,
  };
}

/** Live preference for one Forge/board job. Falls back to empty purpose resolve. */
export function useJobModel(jobId) {
  const purpose = JOB_PURPOSE[jobId] || "";
  const r = useEffective(purpose);
  return {
    loading: r.loading,
    error: r.error,
    armed: r.armed,
    reload: r.reload,
    effective: r.effective,
    provider: viewOfEffective(r.effective, jobId),
  };
}

/** Re-export so a screen that only needs to fire one request can skip the hook. */
export { api };
